import { CefrLevelSchema, FrequencySchema, type Topic, type VocabEntryWithTargets } from "@engo/shared";
import { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button.js";
import { Input } from "../../components/ui/input.js";
import { Modal } from "../../components/ui/Modal.js";
import { Textarea } from "../../components/ui/textarea.js";
import {
  ChunkEditor,
  chunksToDrafts,
  draftsToChunks,
  type ChunkDraft,
} from "../../components/vocab/ChunkEditor.js";
import { ChunkList } from "../../components/vocab/ChunkList.js";
import { TopicPicker } from "../../components/vocab/TopicPicker.js";
import { api } from "../../lib/api.js";
import { buildEditDraft, splitLines, type EditDraft } from "./types.js";

export function WordModal({
  item,
  editDraft,
  isEditMode,
  isSavingEdit,
  isSyncingFailed,
  onClose,
  onSetEditMode,
  onSetEditDraft,
  onAfterMutation,
  setIsSavingEdit,
  setIsSyncingFailed,
}: {
  item: VocabEntryWithTargets;
  editDraft: EditDraft | null;
  isEditMode: boolean;
  isSavingEdit: boolean;
  isSyncingFailed: boolean;
  onClose: () => void;
  onSetEditMode: (v: boolean) => void;
  onSetEditDraft: (v: EditDraft) => void;
  onAfterMutation: () => Promise<void>;
  setIsSavingEdit: (v: boolean) => void;
  setIsSyncingFailed: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const [saveError, setSaveError] = useState<string | null>(null);

  const footer = !isEditMode ? (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          onSetEditDraft(buildEditDraft(item));
          onSetEditMode(true);
        }}
      >
        {t("wordModal.edit")}
      </Button>
      <Button
        variant="secondary"
        disabled={isSyncingFailed}
        onClick={async () => {
          setIsSyncingFailed(true);
          try {
            await api.syncVocab(item.id, true);
            await onAfterMutation();
          } finally {
            setIsSyncingFailed(false);
          }
        }}
      >
        {isSyncingFailed ? t("wordModal.syncing") : t("wordModal.syncFailedTargets")}
      </Button>
    </>
  ) : editDraft ? (
    <>
      <Button type="button" variant="secondary" onClick={() => onSetEditMode(false)}>
        {t("wordModal.cancelEdit")}
      </Button>
      <Button type="submit" form="word-edit-form" variant="primary" disabled={isSavingEdit}>
        {isSavingEdit ? t("wordModal.savingChanges") : t("wordModal.saveChanges")}
      </Button>
    </>
  ) : null;

  return (
    <Modal
      title={item.word}
      headerExtra={<p className="text-sm text-[var(--text-secondary)]">{item.meaning}</p>}
      onClose={onClose}
      maxWidth="max-w-3xl"
      footer={footer}
    >
      {!isEditMode ? (
        <ViewSection item={item} />
      ) : editDraft ? (
        <EditSection
          draft={editDraft}
          saveError={saveError}
          onSave={async (nextDraft) => {
            setIsSavingEdit(true);
            setSaveError(null);
            try {
              const examples = splitLines(nextDraft.examples);
              await api.updateVocab(item.id, {
                entry: {
                  word: nextDraft.word.trim(),
                  type: nextDraft.type.trim(),
                  meaning_simple_en: nextDraft.meaning_simple_en.trim(),
                  ipa: nextDraft.ipa.trim(),
                  core_idea: nextDraft.core_idea.trim(),
                  vietnamese: nextDraft.vietnamese.trim() || null,
                  memory_hook: nextDraft.memory_hook.trim(),
                  examples: examples.length > 0 ? examples : undefined,
                  chunks: nextDraft.chunks.length > 0 ? nextDraft.chunks : undefined,
                  synonyms: splitLines(nextDraft.synonyms),
                  antonyms: splitLines(nextDraft.antonyms),
                  speaking_usage: {
                    casual: nextDraft.speaking_casual.trim() || "Use casually.",
                    neutral: nextDraft.speaking_neutral.trim() || "Use in neutral contexts.",
                    formal: nextDraft.speaking_formal.trim() || "Use formally.",
                  },
                  tags: nextDraft.tags
                    .split(",")
                    .map((x) => x.trim())
                    .filter(Boolean),
                  topics: nextDraft.topics,
                  frequency: nextDraft.frequency,
                  cefr_level: nextDraft.cefr_level,
                },
              });
              await onAfterMutation();
              onSetEditMode(false);
            } catch (err) {
              setSaveError(err instanceof Error ? err.message : t("wordModal.saveFailed"));
            } finally {
              setIsSavingEdit(false);
            }
          }}
        />
      ) : null}
    </Modal>
  );
}

function ViewSection({ item }: { item: VocabEntryWithTargets }) {
  const { t } = useTranslation();
  const [showVietnamese, setShowVietnamese] = useState(false);
  const na = t("common.notAvailable");

  return (
    <>
      <div className="grid gap-3 md:grid-cols-2">
        <Detail label={t("wordModal.partOfSpeech")} value={item.part_of_speech ?? na} />
        <Detail label={t("wordModal.created")} value={new Date(item.created_at).toLocaleString()} />
        <Detail
          label={t("wordModal.topics")}
          value={item.data.topics.map((topic) => t(`topic.${topic}`)).join(", ")}
        />
        <Detail label={t("wordModal.frequency")} value={t(`frequency.${item.data.frequency}`)} />
        <Detail label={t("wordModal.cefrLevel")} value={item.data.cefr_level} />
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <p className="text-xs text-[var(--text-muted)]">{t("wordModal.vietnamese")}</p>
            <button
              type="button"
              onClick={() => setShowVietnamese((v) => !v)}
              className="text-[var(--text-muted)] transition-colors hover:text-[var(--text-secondary)]"
              aria-label={showVietnamese ? t("wordModal.hideVietnamese") : t("wordModal.showVietnamese")}
            >
              {showVietnamese ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            </button>
          </div>
          <p className="text-sm text-[var(--text-secondary)]">
            {showVietnamese ? (item.vietnamese ?? na) : "••••••"}
          </p>
        </div>
        <Detail label={t("wordModal.sourceUrl")} value={item.source_url ?? na} />
      </div>
      <Detail label={t("wordModal.ipa")} value={item.data.ipa} />
      <Detail label={t("wordModal.coreIdea")} value={item.data.core_idea} multiline />
      <Detail label={t("wordModal.memoryHook")} value={item.data.memory_hook} multiline />
      <ListDetail label={t("wordModal.examples")} values={item.data.examples} />
      <ChunkList chunks={item.data.chunks} />
      <ListDetail label={t("wordModal.synonyms")} values={item.data.synonyms} />
      <ListDetail label={t("wordModal.antonyms")} values={item.data.antonyms} />
      <div className="space-y-2">
        <p className="text-xs text-[var(--text-muted)]">{t("wordModal.speakingUsage")}</p>
        <div className="grid gap-2 md:grid-cols-3">
          <Detail label={t("wordModal.casual")} value={item.data.speaking_usage.casual} multiline />
          <Detail label={t("wordModal.neutral")} value={item.data.speaking_usage.neutral} multiline />
          <Detail label={t("wordModal.formal")} value={item.data.speaking_usage.formal} multiline />
        </div>
      </div>
      <TagList tags={item.tags} />
      <TargetList item={item} />
    </>
  );
}

function EditSection({
  draft,
  saveError,
  onSave,
}: {
  draft: EditDraft;
  saveError: string | null;
  onSave: (v: EditDraft) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { register, handleSubmit, reset } = useForm<EditDraft>({
    defaultValues: draft,
  });
  const [chunkDrafts, setChunkDrafts] = useState<ChunkDraft[]>(() => chunksToDrafts(draft.chunks));
  const [topics, setTopics] = useState<Topic[]>(draft.topics);

  useEffect(() => {
    reset(draft);
    setChunkDrafts(chunksToDrafts(draft.chunks));
    setTopics(draft.topics);
  }, [draft, reset]);

  return (
    <form
      id="word-edit-form"
      className="space-y-4"
      onSubmit={(e) => {
        void handleSubmit(async (values) => {
          await onSave({ ...values, chunks: draftsToChunks(chunkDrafts), topics });
        })(e);
      }}
    >
      <p className="text-sm font-medium text-[var(--text-primary)]">{t("wordModal.editContentTitle")}</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label={t("wordModal.fieldWord")}><Input {...register("word")} /></Field>
        <Field label={t("wordModal.fieldPartOfSpeech")}><Input {...register("type")} /></Field>
        <Field label={t("wordModal.fieldMeaning")}><Input {...register("meaning_simple_en")} /></Field>
        <Field label={t("wordModal.fieldIpa")}><Input {...register("ipa")} /></Field>
        <Field label={t("wordModal.fieldVietnamese")}><Input {...register("vietnamese")} /></Field>
        <Field label={t("wordModal.fieldTags")}><Input {...register("tags")} /></Field>
        <Field label={t("wordModal.fieldFrequency")}>
          <select
            {...register("frequency")}
            className="w-full rounded-lg border border-[var(--glass-border)] bg-transparent px-3 py-2 text-sm text-[var(--text-primary)] outline-none ring-0 transition-colors focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/25"
          >
            {FrequencySchema.options.map((option) => (
              <option key={option} value={option} className="bg-[#0f172a] text-white">
                {t(`frequency.${option}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("wordModal.fieldCefrLevel")}>
          <select
            {...register("cefr_level")}
            className="w-full rounded-lg border border-[var(--glass-border)] bg-transparent px-3 py-2 text-sm text-[var(--text-primary)] outline-none ring-0 transition-colors focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/25"
          >
            {CefrLevelSchema.options.map((option) => (
              <option key={option} value={option} className="bg-[#0f172a] text-white">
                {option}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={t("wordModal.fieldTopics")}>
        <TopicPicker value={topics} onChange={setTopics} />
      </Field>
      <Field label={t("wordModal.fieldCoreIdea")}><Textarea rows={2} {...register("core_idea")} /></Field>
      <Field label={t("wordModal.fieldMemoryHook")}><Textarea rows={2} {...register("memory_hook")} /></Field>
      <Field label={t("wordModal.fieldExamplesLines")}><Textarea rows={3} {...register("examples")} /></Field>
      <div className="space-y-1">
        <p className="text-xs text-[var(--text-muted)]">{t("wordModal.fieldChunks")}</p>
        <ChunkEditor value={chunkDrafts} onChange={setChunkDrafts} />
      </div>
      <Field label={t("wordModal.fieldSynonymsLines")}><Textarea rows={3} {...register("synonyms")} /></Field>
      <Field label={t("wordModal.fieldAntonymsLines")}><Textarea rows={3} {...register("antonyms")} /></Field>
      <div className="grid gap-3 md:grid-cols-3">
        <Field label={t("wordModal.fieldSpeakingCasual")}>
          <Textarea rows={3} {...register("speaking_casual")} />
        </Field>
        <Field label={t("wordModal.fieldSpeakingNeutral")}>
          <Textarea rows={3} {...register("speaking_neutral")} />
        </Field>
        <Field label={t("wordModal.fieldSpeakingFormal")}>
          <Textarea rows={3} {...register("speaking_formal")} />
        </Field>
      </div>
      {saveError ? (
        <p className="text-xs text-rose-300">{saveError}</p>
      ) : null}
    </form>
  );
}

function TagList({ tags }: { tags: string[] }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <p className="text-xs text-[var(--text-muted)]">{t("wordModal.tags")}</p>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full bg-[var(--primary)]/10 px-2.5 py-1 text-xs font-medium text-[var(--accent)]"
          >
            #{tag}
          </span>
        ))}
      </div>
    </div>
  );
}

function TargetList({ item }: { item: VocabEntryWithTargets }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <p className="text-xs text-[var(--text-muted)]">{t("wordModal.syncTargets")}</p>
      <div className="space-y-2">
        {item.targets.map((target) => (
          <div key={target.id} className="rounded-lg border border-[var(--glass-border)] p-3">
            <p className="text-sm font-medium text-[var(--text-primary)]">
              {target.kind === "obsidian" ? "Obsidian" : target.kind} - {t(`syncStatus.${target.status}`)}
            </p>
            {target.last_error ? <p className="mt-1 text-xs text-rose-300">{target.last_error}</p> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function ListDetail({ label, values }: { label: string; values: string[] }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-[var(--text-muted)]">{label}</p>
      <ul className="list-disc space-y-1 pl-5 text-sm text-[var(--text-secondary)]">
        {values.map((x, idx) => (
          <li key={`${label}-${idx}`}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-[var(--text-muted)]">{label}</p>
      {children}
    </div>
  );
}

function Detail({ label, value, multiline = false }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-[var(--text-muted)]">{label}</p>
      <p className={multiline ? "whitespace-pre-wrap text-sm text-[var(--text-secondary)]" : "text-sm text-[var(--text-secondary)]"}>
        {value}
      </p>
    </div>
  );
}
